import { ChromeAppWrapperView } from "@util/chrome-extension.util";
import { SidebarLink } from "@clockwise/web-commons/src/ui/scheduling-links-sidebar";

export const chromeExtensionActions = {
  NEW_MESSAGE_EVENT_DATA_FROM_CHROME_EXTENSION: "NEW_MESSAGE_EVENT_DATA_FROM_CHROME_EXTENSION",
  RESET_NOTIFICATION_COUNT: "RESET_NOTIFICATION_COUNT",
  SET_ACTIVE_VIEW_CALENDAR_PROFILE: "SET_ACTIVE_VIEW_CALENDAR_PROFILE",
  SET_ACTIVE_VIEW_EVENT: "SET_ACTIVE_VIEW_EVENT",
  SET_ACTIVE_VIEW: "SET_ACTIVE_VIEW",
  SET_AUTOPILOT_FEED_STATUS_DEFRAG_CARD_NOTIFICATION_COUNT:
    "SET_AUTOPILOT_FEED_STATUS_DEFRAG_CARD_NOTIFICATION_COUNT",
  SET_CALENDAR_URL: "SET_CALENDAR_URL",
  SET_CONTENT_SCROLL_TOP: "SET_CONTENT_SCROLL_TOP",
  SET_IS_ONLINE: "SET_IS_ONLINE",
  SET_LAST_OFFLINE: "SET_LAST_OFFLINE",
  SET_MENU_DRAWER_OPEN: "SET_MENU_DRAWER_OPEN",
  SET_MODE: "SET_MODE",
  SET_SCHEDULER_BUTTON_CLICKED: "SET_SCHEDULER_BUTTON_CLICKED",
  SET_SETTINGS_DIALOG_OPEN: "SET_SETTINGS_DIALOG_OPEN",
  SET_SHARE_BEST_TIMES_LINK: "SET_SHARE_BEST_TIMES_LINK",
  SET_SHOULD_SYNC_ACTIVE_ORG: "SET_SHOULD_SYNC_ACTIVE_ORG",
  SET_SUGGESTIONS_FEED_NOTIFICATION_COUNT: "SET_SUGGESTIONS_FEED_NOTIFICATION_COUNT",
  SHOULD_FETCH_SUGGESTED_TIMES: "SHOULD_FETCH_SUGGESTED_TIMES",
  UNSET_RECENTLY_RESCHEDULED: "UNSET_RECENTLY_RESCHEDULED",
  SET_OPEN_SHARE_LINK_MODAL: "SET_OPEN_SHARE_LINK_MODAL",
  SET_ASSISTANT_FEED_CARD_NOTIFICATION_COUNT: "SET_ASSISTANT_FEED_CARD_NOTIFICATION_COUNT",
};

export interface IChromeExtensionActionData {
  activeView?: ChromeAppWrapperView;
  calendarId?: string;
  calendarUrl?: string;
  contentScrollTop?: number;
  currentViewOfEventWasSetByUrlParam?: boolean;
  externalEventId?: string;
  isOnline?: boolean;
  lastOffline?: number;
  menuDrawerOpen?: boolean;
  mode?: "webapp" | "extension";
  notificationCount?: number;
  openReschedule?: boolean;
  payload?: any; // from post message
  recentlyRescheduled?: boolean;
  schedulerEmails?: string[];
  settingsDialogOpen?: boolean;
  shareBestTimesLink?: SidebarLink | null;
  shouldFetchSuggestedTimes?: boolean;
  shouldSyncActiveOrg?: boolean;
  showClockwiseHistory?: boolean;
  showHeatmap?: boolean;
  topicId?: string;
  type?: string; // from post message
  viewWeek?: string;
  open?: boolean;
}

export interface IChromeExtensionAction {
  type: string;
  data?: IChromeExtensionActionData;
}

export const newMessageEventDataFromChromeExtension = (
  data: IChromeExtensionActionData,
): IChromeExtensionAction => ({
  data,
  type: chromeExtensionActions.NEW_MESSAGE_EVENT_DATA_FROM_CHROME_EXTENSION,
});

export const unsetRecentlyRescheduled = () => ({
  type: chromeExtensionActions.UNSET_RECENTLY_RESCHEDULED,
});

export const setMode = (mode: "webapp" | "extension") => ({
  type: chromeExtensionActions.SET_MODE,
  data: {
    mode,
  },
});

export const setMenuDrawerOpen = (open: boolean) => ({
  type: chromeExtensionActions.SET_MENU_DRAWER_OPEN,
  data: {
    menuDrawerOpen: open,
  },
});

export const setSettingsDialogOpen = (open: boolean) => ({
  type: chromeExtensionActions.SET_SETTINGS_DIALOG_OPEN,
  data: {
    settingsDialogOpen: open,
  },
});

export const setShareBestTimesLink = (link: SidebarLink | null) => ({
  type: chromeExtensionActions.SET_SHARE_BEST_TIMES_LINK,
  data: {
    shareBestTimesLink: link,
  },
});

export const setActiveView = (view: ChromeAppWrapperView) => ({
  type: chromeExtensionActions.SET_ACTIVE_VIEW,
  data: {
    activeView: view,
  },
});

export const setActiveViewFeed = () => ({
  type: chromeExtensionActions.SET_ACTIVE_VIEW,
  data: {
    activeView: ChromeAppWrapperView.Feed,
  },
});

export const setActiveViewDay = () => ({
  type: chromeExtensionActions.SET_ACTIVE_VIEW,
  data: {
    activeView: ChromeAppWrapperView.Day,
  },
});

export const setActiveViewTeamHealth = () => ({
  type: chromeExtensionActions.SET_ACTIVE_VIEW,
  data: {
    activeView: ChromeAppWrapperView.TeamHealth,
  },
});

export const setActiveViewAutopilotList = () => ({
  type: chromeExtensionActions.SET_ACTIVE_VIEW,
  data: {
    activeView: ChromeAppWrapperView.AutopilotList,
  },
});

export const setActiveViewCalendarProfile = (calendarId: string, viewWeek?: string) => ({
  type: chromeExtensionActions.SET_ACTIVE_VIEW_CALENDAR_PROFILE,
  data: {
    calendarId,
    viewWeek,
  },
});

/**
 * @param calendarId
 * Important: Google makes copies of an event for every calendar that the event is on.
 * The event id will the same but the calendarId changes based on which calendar we're looking at.
 * Thus, be mindful when setting calendarId to the current user's calendarId vs the event organizer's calendarId (especially when they're different).
 * Setting the calendarId to event organizer's calendarId blocks the current user from rescheduling the event
 * because Google does not grant the user privileges to reschedule the other's copy of the event...
 * even though the user may be able to reschedule their own copy (which in result would reschedule the other's copy)
 */
export const setActiveViewEvent = (
  externalEventId: string,
  calendarId: string,
  recentlyRescheduled: boolean,
  openReschedule?: boolean,
  showClockwiseHistory?: boolean,
  currentViewOfEventWasSetByUrlParam = false,
) => ({
  type: chromeExtensionActions.SET_ACTIVE_VIEW_EVENT,
  data: {
    externalEventId,
    calendarId,
    recentlyRescheduled,
    openReschedule,
    showClockwiseHistory: !!showClockwiseHistory,
    currentViewOfEventWasSetByUrlParam: !!currentViewOfEventWasSetByUrlParam,
  },
});

export const setIsOnline = (isOnline: boolean) => ({
  type: chromeExtensionActions.SET_IS_ONLINE,
  data: {
    isOnline,
  },
});

export const setLastOffline = (lastOffline: number) => ({
  type: chromeExtensionActions.SET_LAST_OFFLINE,
  data: {
    lastOffline,
  },
});

export const setSchedulerButtonClicked = (schedulerEmails: string[]) => ({
  type: chromeExtensionActions.SET_SCHEDULER_BUTTON_CLICKED,
  data: {
    schedulerEmails,
  },
});

export const setContentScrollTop = (contentScrollTop: number) => ({
  type: chromeExtensionActions.SET_CONTENT_SCROLL_TOP,
  data: {
    contentScrollTop,
  },
});

export const setSuggestionsFeedNotificationCount = (notificationCount: number) => ({
  type: chromeExtensionActions.SET_SUGGESTIONS_FEED_NOTIFICATION_COUNT,
  data: {
    notificationCount,
  },
});

export const setAutopilotFeedStatusDefragCardNotificationCount = (notificationCount: number) => ({
  type: chromeExtensionActions.SET_AUTOPILOT_FEED_STATUS_DEFRAG_CARD_NOTIFICATION_COUNT,
  data: {
    notificationCount,
  },
});

export const setAssistantFeedCardNotificationCount = (notificationCount: number) => ({
  type: chromeExtensionActions.SET_ASSISTANT_FEED_CARD_NOTIFICATION_COUNT,
  data: {
    notificationCount,
  },
});

export const resetNotificationCount = () => ({
  type: chromeExtensionActions.RESET_NOTIFICATION_COUNT,
});

export const setShouldSyncActiveOrg = (shouldSyncActiveOrg: boolean) => ({
  type: chromeExtensionActions.SET_SHOULD_SYNC_ACTIVE_ORG,
  data: {
    shouldSyncActiveOrg,
  },
});

export const setShouldFetchSuggestedTimes = (shouldFetchSuggestedTimes: boolean) => ({
  type: chromeExtensionActions.SHOULD_FETCH_SUGGESTED_TIMES,
  data: {
    shouldFetchSuggestedTimes,
  },
});

export const setCalendarUrl = (calendarUrl: string) => ({
  type: chromeExtensionActions.SET_CALENDAR_URL,
  data: {
    calendarUrl,
  },
});

export const setOpenShareLinkModal = (open: boolean) => ({
  type: chromeExtensionActions.SET_OPEN_SHARE_LINK_MODAL,
  data: {
    open,
  },
});
